/**
 * Weather Store
 * Manages weather data state using Pinia
 */

import { defineStore } from "pinia";
import { ref, shallowRef, computed } from "vue";
import type {
  MainWeatherInfo,
  ROLocation,
  RainViewerFrame,
} from "rainowls-shared";
import { createLatestRequest } from "../utils/latestRequest";
import {
  fahrenheitToCelsius,
  observedRainWindow,
  RainOwlsMain,
  RONetworkManager,
} from "rainowls-shared";
import type { RadarTimestamp } from "@/utils/radarProvider";
import { fetchNWSRadarTimestamps } from "@/services/nwsRadar.service";
import { fetchWeathercomRadarTimestamps } from "@/services/weathercomRadar.service";
import { setOpenMeteoEndpoint } from "@/services/weather.service";
import { useSettingsStore } from "@/stores/settings.store";

import {
  validSummaryCurrent,
  type LocationWeatherSummary,
} from "@/utils/locationSummary";
export type { LocationWeatherSummary } from "@/utils/locationSummary";

// Cache duration: 10 minutes in milliseconds
const CACHE_DURATION_MS = 10 * 60 * 1000;

export const useWeatherStore = defineStore("weather", () => {
  const currentWeather = shallowRef<MainWeatherInfo | null>(null);
  const hourlyWeather = computed(() => currentWeather.value?.hours ?? null);
  const dailyWeather = computed(
    () => currentWeather.value?.dailyWeather ?? null,
  );
  // Build station history once per snapshot; renderers only format its prepared slots.
  const rainObservations = computed(() => {
    const history = currentWeather.value?.observedRainHistory;
    return history ? observedRainWindow(history) : null;
  });
  const requests = createLatestRequest<MainWeatherInfo>();
  let requestVersion = 0;
  let cachedSourceKey = "";
  const isRefreshing = ref(false);
  const radarTimestamps = ref<RainViewerFrame[]>([]);
  const nwsRadarTimestamps = ref<RadarTimestamp[]>([]);
  const weathercomRadarTimestamps = ref<RadarTimestamp[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref<Date | null>(null);
  const lastFetchedLocationId = ref<string | null>(null);

  // Weather summaries for saved locations (keyed by location id)
  const locationSummaries = shallowRef<Map<string, LocationWeatherSummary>>(
    new Map(),
  );
  const summariesLoading = ref(false);

  const hasWeatherData = computed(() => currentWeather.value !== null);
  const temperature = computed(() => currentWeather.value?.temperature ?? null);
  const windSpeed = computed(() => currentWeather.value?.windSpeed ?? null);
  const windDirection = computed(
    () => currentWeather.value?.windDirection ?? null,
  );
  const humidity = computed(() => currentWeather.value?.humidity ?? null);
  const pressure = computed(() => currentWeather.value?.pressure ?? null);
  const weatherCode = computed(() => currentWeather.value?.weatherCode ?? null);
  const isDay = computed(() => currentWeather.value?.isDay ?? true);

  let summariesKey = "";
  let summariesFetchedAt = 0;
  let summariesVersion = 0;
  let pendingSummaries: { key: string; promise: Promise<void> } | null = null;
  let radarRequest: Promise<void> | null = null;
  let radarFetchedAt = 0;
  let weathercomRadarRequest: Promise<void> | null = null;
  let weathercomCheckedSlot: number | null = null;

  /** Reuse a complete snapshot only for the same location within ten minutes. */
  function isCacheValid(location: ROLocation): boolean {
    // No cached data
    if (!lastUpdated.value || !currentWeather.value) {
      return false;
    }

    // Location changed
    const locationId = `${location.lat}-${location.lng}`;
    if (lastFetchedLocationId.value !== locationId) {
      return false;
    }

    // Check if cache has expired
    const now = new Date();
    const elapsed = now.getTime() - lastUpdated.value.getTime();
    return elapsed < CACHE_DURATION_MS;
  }

  /** Fetch the active location once per request key and discard stale results. */
  async function fetchWeather(
    location: ROLocation,
    forceRefresh = false,
  ): Promise<void> {
    const settings = useSettingsStore();
    const mock = settings.useMockData;
    const privateEndpoint = settings.usePrivateOpenMeteo;
    const conditionKey = settings.mockWeatherCondition;
    const sourceKey = mock
      ? `mock:${conditionKey}`
      : `live:${settings.usePrivateOpenMeteo}`;
    const locationId = `${location.lat}-${location.lng}`;
    const version = ++requestVersion;

    if (
      !forceRefresh &&
      cachedSourceKey === sourceKey &&
      isCacheValid(location)
    ) {
      requests.invalidate();
      isLoading.value = false;
      isRefreshing.value = false;
      error.value = null;
      return;
    }
    const sameLocation =
      lastFetchedLocationId.value === locationId &&
      cachedSourceKey === sourceKey;
    if (!sameLocation) currentWeather.value = null;
    // Keep a successful snapshot visible during background refreshes.
    isLoading.value = !sameLocation || !currentWeather.value;
    isRefreshing.value = !isLoading.value;
    error.value = null;
    try {
      const result = await requests.run(
        `${locationId}:${sourceKey}`,
        async () => {
          if (mock) {
            const { MockDataService, WeatherConditions } =
              await import("@/services/MockDataService");
            const condition =
              WeatherConditions[
                conditionKey as keyof typeof WeatherConditions
              ] ?? WeatherConditions.rain;
            const data = MockDataService.generateMainWeather(
              condition.weatherCode,
              condition.isDay,
            );
            data.locationName = location.name || "Mock Location";
            return data;
          }
          // RainOwlsMain owns mutable model instances: never run it concurrently.
          setOpenMeteoEndpoint(privateEndpoint);
          const service = RainOwlsMain.getInstance();
          await service.fetchWeather({ ...location });
          return service.homeWeather;
        },
      );
      if (!result || version !== requestVersion) return;
      if (result.availabilityError) {
        error.value = result.availabilityError;
        // Keep a complete same-location snapshot if the refresh is incomplete.
        // Partial data can be shown on first load, but must not enter the cache.
        if (
          !currentWeather.value &&
          (result.currentObservationSource || result.hours?.hours?.length)
        ) {
          currentWeather.value = result;
          lastFetchedLocationId.value = locationId;
          cachedSourceKey = sourceKey;
        }
        lastUpdated.value = null;
        return;
      }
      currentWeather.value = result;
      lastUpdated.value = new Date();
      lastFetchedLocationId.value = locationId;
      cachedSourceKey = sourceKey;
    } catch (cause) {
      if (version === requestVersion)
        error.value =
          cause instanceof Error
            ? cause.message
            : "Failed to fetch weather data";
    } finally {
      if (version === requestVersion) {
        isLoading.value = false;
        isRefreshing.value = false;
      }
    }
  }

  /** Batch saved-place summaries and retain only matching stale entries. */
  function fetchLocationSummaries(
    locations: { id: string; location: ROLocation }[],
  ): Promise<void> {
    if (!locations.length || useSettingsStore().useMockData) {
      summariesVersion++;
      summariesKey = "";
      summariesFetchedAt = 0;
      pendingSummaries = null;
      summariesLoading.value = false;
      locationSummaries.value = new Map();
      return Promise.resolve();
    }
    // Order and pinning affect presentation, not the weather cache identity.
    const key = JSON.stringify(
      locations
        .map(({ id, location }) => [id, location.lat, location.lng])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    );
    if (pendingSummaries?.key === key) return pendingSummaries.promise;
    if (
      key === summariesKey &&
      Date.now() - summariesFetchedAt < CACHE_DURATION_MS
    )
      return Promise.resolve();
    const version = ++summariesVersion;
    summariesKey = key;
    summariesFetchedAt = 0;
    summariesLoading.value = true;
    // Never retain weather for removed places or changed coordinates.
    const retained = new Map<string, LocationWeatherSummary>();
    for (const { id, location } of locations) {
      const old = locationSummaries.value.get(id);
      if (old && old.lat === location.lat && old.lng === location.lng)
        retained.set(id, { ...old, stale: true });
    }
    locationSummaries.value = retained;
    const batches = Array.from(
      { length: Math.ceil(locations.length / 10) },
      (_, index) => locations.slice(index * 10, (index + 1) * 10),
    );
    const promise = (async () => {
      try {
        const network = RONetworkManager.getInstance();
        const results = await Promise.allSettled(
          batches.map((batch) =>
            network.fetchCurrentSummaries(batch.map((item) => item.location)),
          ),
        );
        if (version !== summariesVersion) return;
        const summaries = new Map(retained);
        let freshCount = 0;
        results.forEach((response, batchIndex) => {
          if (response.status !== "fulfilled" || !response.value.data?.length)
            return;
          const batch = batches[batchIndex]!;
          response.value.data.forEach((weather, index) => {
            const item = batch[index];
            if (!item || !validSummaryCurrent(weather.current)) return;
            summaries.set(item.id, {
              temperature: fahrenheitToCelsius(weather.current.temperature),
              weatherCode: weather.current.weatherCode,
              isDay: weather.current.isDay === 1,
              fetchedAt: Date.now(),
              stale: false,
              lat: item.location.lat,
              lng: item.location.lng,
            });
            freshCount++;
          });
        });
        locationSummaries.value = summaries;
        if (freshCount === locations.length) summariesFetchedAt = Date.now();
      } finally {
        if (version === summariesVersion) {
          summariesLoading.value = false;
          pendingSummaries = null;
        }
      }
    })();
    pendingSummaries = { key, promise };
    return promise;
  }

  /** Read a saved place's latest summary without triggering a request. */
  function getLocationSummary(id: string): LocationWeatherSummary | undefined {
    return locationSummaries.value.get(id);
  }

  /** Fetch RainViewer's provider-supplied frame paths. */
  async function fetchRadarTimestamps(): Promise<void> {
    try {
      const networkManager = RONetworkManager.getInstance();
      radarTimestamps.value = await networkManager.fetchRainViewerFrames();
    } catch (e) {
      console.error("Failed to fetch radar timestamps:", e);
      radarTimestamps.value = [];
    }
  }

  /** Fetch only NWS frames advertised by its capabilities endpoint. */
  async function fetchNWSRadarTimestampsAction(): Promise<void> {
    try {
      nwsRadarTimestamps.value = await fetchNWSRadarTimestamps();
    } catch (e) {
      console.error("Failed to fetch NWS radar timestamps:", e);
      nwsRadarTimestamps.value = [];
    }
  }

  /** Fetch one inventory per five-minute slot and retain frames if it fails. */
  function fetchWeathercomRadarTimestampsAction(): Promise<void> {
    if (weathercomRadarRequest) return weathercomRadarRequest;
    const slot = Math.floor((Date.now() - 3 * 60_000) / (5 * 60_000));
    if (slot === weathercomCheckedSlot) return Promise.resolve();
    weathercomCheckedSlot = slot;
    weathercomRadarRequest = (async () => {
      try {
        weathercomRadarTimestamps.value = await fetchWeathercomRadarTimestamps();
      } catch (e) {
        console.warn("Weather.com radar inventory unavailable; keeping previous frames:", e);
      } finally {
        weathercomRadarRequest = null;
      }
    })();
    return weathercomRadarRequest;
  }

  /** Coalesce radar metadata requests for all three providers. */
  function fetchAllRadarTimestamps(): Promise<void> {
    if (radarRequest) return radarRequest;
    if (Date.now() - radarFetchedAt < 2 * 60 * 1000) {
      return fetchWeathercomRadarTimestampsAction();
    }
    radarRequest = Promise.all([
      fetchWeathercomRadarTimestampsAction(),
      fetchRadarTimestamps(),
      fetchNWSRadarTimestampsAction(),
    ])
      .then(() => {
        radarFetchedAt = Date.now();
      })
      .finally(() => {
        radarRequest = null;
      });
    return radarRequest;
  }

  /** Set the initial loading state while the session resolves a location. */
  function setLoading(value: boolean): void {
    isLoading.value = value;
  }

  /** Clear the active snapshot and invalidate any outstanding weather result. */
  function clearWeatherData(): void {
    currentWeather.value = null;
    requests.invalidate();
    requestVersion++;
    isLoading.value = false;
    isRefreshing.value = false;
    error.value = null;
    lastUpdated.value = null;
    lastFetchedLocationId.value = null;
  }

  return {
    // State
    currentWeather,
    hourlyWeather,
    dailyWeather,
    rainObservations,
    radarTimestamps,
    nwsRadarTimestamps,
    weathercomRadarTimestamps,
    fetchWeathercomRadarTimestampsAction,
    isLoading,
    isRefreshing,
    error,
    lastUpdated,

    // Getters
    hasWeatherData,
    temperature,
    windSpeed,
    windDirection,
    humidity,
    pressure,
    weatherCode,
    isDay,

    // Actions
    setLoading,
    fetchWeather,
    fetchLocationSummaries,
    getLocationSummary,
    fetchRadarTimestamps,
    fetchNWSRadarTimestamps: fetchNWSRadarTimestampsAction,
    fetchAllRadarTimestamps,
    clearWeatherData,

    // Location summaries
    locationSummaries,
    summariesLoading,
  };
});
